import { t } from "i18next";
import { toast } from "sonner";

import BulkAPI from "../../../api/BulkAPI";

import type {
  QueryClient,
  QueryKey,
  UseMutationOptions,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

import type {
  BulkFileInfo,
  GetBulkStatusResponse,
  RunBulkIngestResponse,
} from "../../../api/BulkAPI.types";

const UPLOAD_INVALID_FILE_STATUS = 400;

const UPLOAD_TOO_LARGE_STATUS = 413;

const STOREFRONT_PROJECT_LOCKED_STATUS = 409;

const STOREFRONT_PROJECT_REQUIRED_STATUS = 422;

const STOREFRONT_PROJECT_REQUIRED_ERROR = "storefront_project_required";

interface UploadBulkMutationOptions {
  queryClient: QueryClient;
  onProgress: (percent: number) => void;
  onUploaded: (bulkId: string) => void;
}

interface RunBulkIngestMutationOptions {
  bulkId: string;
  queryClient: QueryClient;
}

interface SetBulkStorefrontProjectMutationOptions {
  bulkId: string;
  queryClient: QueryClient;
}

interface StorefrontProjectRequiredErrorResponse {
  error: typeof STOREFRONT_PROJECT_REQUIRED_ERROR;
}

interface ErrorMessageResponse {
  message: string;
}

function getIndexQueryKey(): QueryKey {
  return BulkAPI.generateQuery({
    fn: BulkAPI.getIndexBulkList,
    params: [],
  }).queryKey;
}

function getStatusQueryKey(bulkId: string): QueryKey {
  return BulkAPI.generateQuery({
    fn: BulkAPI.getBulkStatus,
    params: [{ bulkId }],
  }).queryKey;
}

function isStorefrontProjectRequiredErrorResponse(
  body: unknown,
): body is StorefrontProjectRequiredErrorResponse {
  return (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    body.error === STOREFRONT_PROJECT_REQUIRED_ERROR
  );
}

function isErrorMessageResponse(body: unknown): body is ErrorMessageResponse {
  return (
    typeof body === "object" &&
    body !== null &&
    "message" in body &&
    typeof body.message === "string"
  );
}

function getUploadErrorMessage(error: AxiosError): string {
  const { response } = error;

  if (response?.status === UPLOAD_TOO_LARGE_STATUS) {
    return t("editor-errors:errorUploadSize");
  }

  const message =
    response?.status === UPLOAD_INVALID_FILE_STATUS &&
    isErrorMessageResponse(response.data)
      ? response.data.message.trim()
      : "";

  return message.length > 0 ? message : t("editor-errors:errorUpload");
}

export function uploadBulkMutation({
  queryClient,
  onProgress,
  onUploaded,
}: UploadBulkMutationOptions): UseMutationOptions<
  GetBulkStatusResponse,
  AxiosError,
  BulkFileInfo
> {
  return {
    mutationFn: async (file) =>
      BulkAPI.uploadBulk({
        file,
        onUploadProgress: (event) => {
          onProgress(
            event.lengthComputable && event.total > 0
              ? Math.floor((event.loaded * 100) / event.total)
              : 0,
          );
        },
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries(getIndexQueryKey());
      toast.success(t("editor-bulk:upload-json-success"));
      onUploaded(response.more.bulk_ingest_uuid);
    },
    onError: (error) => {
      onProgress(0);
      toast.error(getUploadErrorMessage(error));
    },
  };
}

export function runBulkIngestMutation({
  bulkId,
  queryClient,
}: RunBulkIngestMutationOptions): UseMutationOptions<
  RunBulkIngestResponse,
  AxiosError
> {
  const statusQueryKey = getStatusQueryKey(bulkId);

  return {
    mutationFn: async () => BulkAPI.runBulkIngest({ bulkId }),
    onSuccess: () => {
      queryClient.invalidateQueries(statusQueryKey);
      queryClient.invalidateQueries(getIndexQueryKey());
    },
    onError: (error) => {
      if (
        error.response?.status === STOREFRONT_PROJECT_REQUIRED_STATUS &&
        isStorefrontProjectRequiredErrorResponse(error.response.data)
      ) {
        toast.error(t("editor-bulk:storefrontProjectRequired"));
        queryClient.invalidateQueries(statusQueryKey);
        return;
      }

      toast.error(t("editor-bulk:startIngestError"));
    },
  };
}

export function setBulkStorefrontProjectMutation({
  bulkId,
  queryClient,
}: SetBulkStorefrontProjectMutationOptions): UseMutationOptions<
  GetBulkStatusResponse,
  AxiosError,
  string
> {
  const statusQueryKey = getStatusQueryKey(bulkId);

  return {
    mutationFn: async (sfpUuid) =>
      BulkAPI.setBulkStorefrontProject({ bulkId, sfpUuid }),
    onSuccess: async (response) => {
      await queryClient.cancelQueries(statusQueryKey);
      queryClient.setQueryData(statusQueryKey, response);
    },
    onError: (error) => {
      if (error.response?.status === STOREFRONT_PROJECT_LOCKED_STATUS) {
        toast.error(t("editor-bulk:storefrontProjectLocked"));
        queryClient.invalidateQueries(statusQueryKey);
        return;
      }

      toast.error(t("editor-bulk:storefrontProjectError"));
    },
  };
}
